import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import useViewportSize from "@/hooks/useViewportSize";
import MobileSlider from "@/components/mobileSlider/MobileSlider";
import Popup from "@/components/popup/Popup";
import CalendarIcon from "../../icons/CalendarIcon";
import { useGetUserShopItems } from "../../hooks/useShop";
import LoadingPage from "@/components/loadingPage/LoadingPage";
import { IShopHistoryItem, PurchaseStatusEnum } from "../../types";

import styles from "./ShopHistory.module.scss";
import ShopHistoryItem from "./ShopHistoryItem";

interface ShopHistoryProps {
  isOpen: boolean;
  onClose: () => void;
  className?: string;
  headerClassName?: string;
}

const ShopHistory: React.FC<ShopHistoryProps> = ({
  isOpen,
  onClose,
  className = "",
  headerClassName = "",
}) => {
  const { t } = useTranslation();
  const { width } = useViewportSize();
  const { data, isLoading } = useGetUserShopItems();
  const purchasedHistory = useMemo(() => {
    if (!data) return {};

    return Object.entries(data).reduce(
      (acc, [date, items]) => {
        const purchasedItems = items.filter((item) => item.status === PurchaseStatusEnum.SUCCESS);

        if (purchasedItems.length) {
          acc[date] = purchasedItems;
        }

        return acc;
      },
      {} as Record<string, IShopHistoryItem[]>
    );
  }, [data]);
  const historyDates = Object.keys(purchasedHistory);

  const renderContent = () => (
    <div className={styles.historyContent}>
      {historyDates.length > 0 ? (
        historyDates.map((date) => (
          <div key={date} className={styles.dateSection}>
            <div className={styles.dateHeader}>
              <CalendarIcon />
              <span className={styles.dateText}>{date}</span>
            </div>
            <div className={styles.historyItems}>
              {purchasedHistory[date].map((item) => (
                <ShopHistoryItem key={item.id} data={item} />
              ))}
            </div>

            {width < 800 && <div className={styles.mobileDivider} />}
          </div>
        ))
      ) : (
        <p className={styles.noHistoryText}>{t("shop.shopHistoryEmpty")}</p>
      )}
    </div>
  );

  const modalTitle = (
    <div className={styles.headerContainer}>
      <p className={styles.headerTitle}>{t("shop.shopHistory")}</p>
    </div>
  );

  if (!isOpen) return null;

  return width >= 800 ? (
    <Popup
      className={`${styles.popup} ${className}`}
      title={modalTitle}
      headerClassName={`${styles.modalHeader} ${headerClassName}`}
      isOpen={isOpen}
      onClose={onClose}
      contentClassName="pt-0"
    >
      {isLoading ? <LoadingPage className={styles.loadingPage} /> : renderContent()}
    </Popup>
  ) : (
    <MobileSlider
      className={`${styles.popup} ${className}`}
      title={t("shop.shopHistory")}
      headerClassName={`${styles.modalHeader} ${headerClassName}`}
      isOpen={isOpen}
      onClose={onClose}
      contentClassName="pt-0"
    >
      {isLoading ? <LoadingPage className={styles.loadingPage} /> : renderContent()}
    </MobileSlider>
  );
};

export default ShopHistory;
